import BaseController from "confinus_ui/base_controller";

import type { UomClassification } from "./product_wizard/constants";
import {
  restoreFromUrl,
  pushUrlState,
  debouncedReplaceUrlState,
  handlePopState,
} from "./product_wizard/url_state";
import {
  populateReview,
  updateVariantPreviewCards,
  updateImagePreview,
} from "./product_wizard/review_builder";
import { calculateConversion as doCalculateConversion } from "./product_wizard/conversion_calculator";
import {
  getSearchableSelectLabel,
  getCurrentPriceUom,
} from "./product_wizard/helpers";

export default class extends BaseController {
  static targets = [
    "step",
    "stepIndicator",
    "prevButton",
    "nextButton",
    "submitButton",
    "variantsContainer",
    "variantTemplate",
    "variantCount",
    "priceUomSelect",
    "uomData",
    // Preview targets
    "previewSku",
    "previewTitle",
    "previewDescription",
    "previewCategory",
    "previewBrand",
    "previewCountryOfOrigin",
    "previewFreshOrFrozen",
    "previewWildOrFarmed",
    "previewPriceUom",
    "previewVariantsList",
    "previewImages",
    "previewImagesSection",
    "stepError",
    "reviewContent",
    "previewPanel",
  ];

  static values = {
    currentStep: { type: Number, default: 1 },
    totalSteps: { type: Number, default: 8 },
    variantIndex: { type: Number, default: 0 },
    conversionUrl: { type: String, default: "" },
  };

  declare readonly stepTargets: HTMLElement[];
  declare readonly stepIndicatorTargets: HTMLElement[];
  declare readonly prevButtonTarget: HTMLElement;
  declare readonly nextButtonTarget: HTMLElement;
  declare readonly submitButtonTarget: HTMLElement;
  declare readonly variantsContainerTarget: HTMLElement;
  declare readonly variantTemplateTarget: HTMLTemplateElement;
  declare readonly variantCountTarget: HTMLElement;
  declare readonly uomDataTarget: HTMLScriptElement;
  declare readonly hasPreviewSkuTarget: boolean;
  declare readonly previewSkuTarget: HTMLElement;
  declare readonly hasPreviewTitleTarget: boolean;
  declare readonly previewTitleTarget: HTMLElement;
  declare readonly hasPreviewDescriptionTarget: boolean;
  declare readonly previewDescriptionTarget: HTMLElement;
  declare readonly hasPreviewCategoryTarget: boolean;
  declare readonly previewCategoryTarget: HTMLElement;
  declare readonly hasPreviewBrandTarget: boolean;
  declare readonly previewBrandTarget: HTMLElement;
  declare readonly hasPreviewCountryOfOriginTarget: boolean;
  declare readonly previewCountryOfOriginTarget: HTMLElement;
  declare readonly hasPreviewFreshOrFrozenTarget: boolean;
  declare readonly previewFreshOrFrozenTarget: HTMLElement;
  declare readonly hasPreviewWildOrFarmedTarget: boolean;
  declare readonly previewWildOrFarmedTarget: HTMLElement;
  declare readonly hasPreviewPriceUomTarget: boolean;
  declare readonly previewPriceUomTarget: HTMLElement;
  declare readonly hasPreviewVariantsListTarget: boolean;
  declare readonly previewVariantsListTarget: HTMLElement;
  declare readonly hasPreviewImagesTarget: boolean;
  declare readonly previewImagesTarget: HTMLElement;
  declare readonly hasPreviewImagesSectionTarget: boolean;
  declare readonly previewImagesSectionTarget: HTMLElement;
  declare readonly hasPriceUomSelectTarget: boolean;
  declare readonly priceUomSelectTarget: HTMLSelectElement;
  declare readonly hasStepErrorTarget: boolean;
  declare readonly stepErrorTarget: HTMLElement;
  declare readonly hasPreviewPanelTarget: boolean;
  declare readonly previewPanelTarget: HTMLElement;
  declare readonly hasReviewContentTarget: boolean;
  declare readonly reviewContentTarget: HTMLElement;

  declare currentStepValue: number;
  declare totalStepsValue: number;
  declare variantIndexValue: number;
  declare conversionUrlValue: string;

  private uomClassification: UomClassification | null = null;
  private popstateHandler: ((event: PopStateEvent) => void) | null = null;
  urlUpdateTimer: ReturnType<typeof setTimeout> | null = null;

  // --- Lifecycle ---

  connect() {
    this.loadUomData();

    // Restore step immediately from URL (no child dependency)
    const stepParam = parseInt(
      new URLSearchParams(window.location.search).get("step") || "",
      10,
    );
    if (stepParam >= 1 && stepParam <= this.totalStepsValue) {
      this.currentStepValue = stepParam;
    }

    this.showCurrentStep();
    this.updateNavButtons();

    // Defer form value restore so child SearchableSelect controllers have time to connect.
    requestAnimationFrame(() => {
      restoreFromUrl(this);
      this.showCurrentStep();
      this.updateNavButtons();
      this.updatePreview();
    });

    // Listen for browser back/forward
    this.popstateHandler = (event: PopStateEvent) =>
      handlePopState(this, event);
    window.addEventListener("popstate", this.popstateHandler);
  }

  disconnect() {
    if (this.popstateHandler) {
      window.removeEventListener("popstate", this.popstateHandler);
    }
    if (this.urlUpdateTimer) {
      clearTimeout(this.urlUpdateTimer);
    }
  }

  // Called when a SearchableSelect finishes async label resolution.
  onLabelResolved() {
    if (this.currentStepValue === this.totalStepsValue) {
      populateReview(this);
    }
    this.updatePreview();
  }

  // --- Step navigation ---

  next() {
    if (!this.validateCurrentStep()) return;
    if (this.currentStepValue < this.totalStepsValue) {
      this.currentStepValue++;
      this.showCurrentStep();
      this.updateNavButtons();
      pushUrlState(this);
    }
  }

  previous() {
    this.clearStepError();
    if (this.currentStepValue > 1) {
      this.currentStepValue--;
      this.showCurrentStep();
      this.updateNavButtons();
      pushUrlState(this);
    }
  }

  goToStep(event: Event) {
    this.clearStepError();
    const target = event.currentTarget as HTMLElement;
    const step = parseInt(target.dataset.step || "1", 10);
    if (step >= 1 && step <= this.totalStepsValue) {
      if (step > this.currentStepValue && !this.validateCurrentStep()) return;
      this.currentStepValue = step;
      this.showCurrentStep();
      this.updateNavButtons();
      pushUrlState(this);
    }
  }

  // --- Form submission validation ---

  validateBeforeSubmit(event: Event) {
    // Check if any variant has an active UoM warning
    const warnings = this.variantsContainerTarget.querySelectorAll(
      '[data-platform-admin--variant-form-target="uomWarning"]:not(.hidden)',
    );
    if (warnings.length > 0) {
      event.preventDefault();
      this.showStepError(
        "Cannot create product: one or more variants have UoM compatibility issues. Please fix the warnings above.",
      );
      this.currentStepValue = this.totalStepsValue - 1;
      this.showCurrentStep();
      this.updateNavButtons();
      return;
    }

    // Check at least one variant exists
    const variantCount = this.variantsContainerTarget.querySelectorAll(
      "[data-variant-entry]",
    ).length;
    if (variantCount === 0) {
      event.preventDefault();
      this.showStepError("At least one variant is required.");
      return;
    }
  }

  // --- Variant management ---

  addVariant() {
    const template = this.variantTemplateTarget;

    const html = template.innerHTML.replace(
      /__VARIANT_INDEX__/g,
      String(this.variantIndexValue),
    );

    const wrapper = document.createElement("div");
    wrapper.innerHTML = html;
    const variantEl = wrapper.firstElementChild as HTMLElement;
    if (variantEl) {
      const priceUom = getCurrentPriceUom(this);
      if (priceUom) {
        variantEl.setAttribute(
          "data-platform-admin--variant-form-price-uom-value",
          priceUom,
        );
      }
      this.variantsContainerTarget.appendChild(variantEl);
    }

    this.variantIndexValue++;
    this.updateVariantCount();
  }

  removeVariant(event: Event) {
    const button = event.currentTarget as HTMLElement;
    const variantEntry = button.closest(
      "[data-variant-entry]",
    ) as HTMLElement | null;
    if (variantEntry) {
      variantEntry.remove();
      this.updateVariantCount();
    }
  }

  // --- Price UoM dispatch ---

  priceUomSelected(event: CustomEvent) {
    const priceUom = event.detail?.id || "";
    this.updateVariantPriceUom(String(priceUom));
  }

  priceUomCleared() {
    this.updateVariantPriceUom("");
  }

  priceUomChanged(event: Event) {
    const select = event.target as HTMLSelectElement;
    this.updateVariantPriceUom(select.value);
  }

  // --- Conversion calculator (delegated) ---

  calculateConversion(event: Event) {
    doCalculateConversion(this, event);
  }

  // --- Preview updates + URL sync ---

  updatePreview() {
    const form = this.element.querySelector("form");
    if (!form) return;

    const getValue = (name: string): string => {
      const input = form.querySelector(
        `[name*="[${name}]"]`,
      ) as HTMLInputElement | null;
      return input?.value || "";
    };

    if (this.hasPreviewSkuTarget) {
      this.previewSkuTarget.textContent = getValue("sku") || "\u2014";
    }
    if (this.hasPreviewTitleTarget) {
      this.previewTitleTarget.textContent =
        getValue("short_description") || "\u2014";
    }
    if (this.hasPreviewDescriptionTarget) {
      this.previewDescriptionTarget.textContent =
        getValue("medium_description") || "";
    }

    if (this.hasPreviewCategoryTarget) {
      this.previewCategoryTarget.textContent =
        getSearchableSelectLabel(this, "product_category_id") || "\u2014";
    }
    if (this.hasPreviewBrandTarget) {
      this.previewBrandTarget.textContent =
        getSearchableSelectLabel(this, "product_brand_id") || "\u2014";
    }
    if (this.hasPreviewCountryOfOriginTarget) {
      this.previewCountryOfOriginTarget.textContent =
        getSearchableSelectLabel(this, "country_of_origin") || "\u2014";
    }
    if (this.hasPreviewFreshOrFrozenTarget) {
      this.previewFreshOrFrozenTarget.textContent =
        getSearchableSelectLabel(this, "fresh_or_frozen") || "\u2014";
    }
    if (this.hasPreviewWildOrFarmedTarget) {
      this.previewWildOrFarmedTarget.textContent =
        getSearchableSelectLabel(this, "wild_or_farmed") || "\u2014";
    }
    if (this.hasPreviewPriceUomTarget) {
      this.previewPriceUomTarget.textContent =
        getSearchableSelectLabel(this, "default_price_uom") || "\u2014";
    }

    if (this.hasPreviewVariantsListTarget) {
      updateVariantPreviewCards(this);
    }

    updateImagePreview(this);
    debouncedReplaceUrlState(this);
  }

  // --- Public helpers (called by extracted modules via controller reference) ---

  showCurrentStep() {
    this.stepTargets.forEach((step, index) => {
      if (index + 1 === this.currentStepValue) {
        step.classList.remove("hidden");
      } else {
        step.classList.add("hidden");
      }
    });

    const isReviewStep = this.currentStepValue === this.totalStepsValue;
    if (isReviewStep) {
      populateReview(this);
    }

    if (this.hasPreviewPanelTarget) {
      this.previewPanelTarget.style.display = isReviewStep ? "none" : "";
    }

    this.stepIndicatorTargets.forEach((indicator, index) => {
      const stepNum = index + 1;
      indicator.classList.remove(
        "bg-primary-600",
        "text-white",
        "bg-primary-100",
        "text-primary",
        "bg-gray-100",
        "text-gray-500",
      );
      if (stepNum === this.currentStepValue) {
        indicator.classList.add("bg-primary-600", "text-white");
      } else if (stepNum < this.currentStepValue) {
        indicator.classList.add("bg-primary-100", "text-primary");
      } else {
        indicator.classList.add("bg-gray-100", "text-gray-500");
      }
    });
  }

  updateNavButtons() {
    this.prevButtonTarget.classList.toggle(
      "invisible",
      this.currentStepValue === 1,
    );
    this.nextButtonTarget.classList.toggle(
      "hidden",
      this.currentStepValue === this.totalStepsValue,
    );
    this.submitButtonTarget.classList.toggle(
      "hidden",
      this.currentStepValue !== this.totalStepsValue,
    );
  }

  updateVariantCount() {
    const count = this.variantsContainerTarget.querySelectorAll(
      "[data-variant-entry]",
    ).length;
    this.variantCountTarget.textContent = String(count);
  }

  clearStepError() {
    if (this.hasStepErrorTarget) {
      this.stepErrorTarget.textContent = "";
      this.stepErrorTarget.classList.add("hidden");
    }
  }

  // --- Private helpers ---

  private updateVariantPriceUom(priceUom: string) {
    const variants = this.variantsContainerTarget.querySelectorAll(
      "[data-variant-entry]",
    );
    variants.forEach((variant) => {
      variant.setAttribute(
        "data-platform-admin--variant-form-price-uom-value",
        priceUom,
      );
    });

    this.element.dispatchEvent(
      new CustomEvent("product-wizard:price-uom-changed", {
        bubbles: true,
        detail: { priceUom, uomClassification: this.uomClassification },
      }),
    );
  }

  private validateCurrentStep(): boolean {
    const form = this.element.querySelector("form");
    if (!form) return true;

    const getValue = (name: string): string => {
      const input = form.querySelector(
        `[name*="[${name}]"]`,
      ) as HTMLInputElement | null;
      return input?.value?.trim() || "";
    };

    let error = "";

    switch (this.currentStepValue) {
      case 1:
        if (!getValue("sku") || getValue("sku").length < 2)
          error = "SKU is required (minimum 2 characters)";
        else if (
          !getValue("short_description") ||
          getValue("short_description").length < 4
        )
          error = "Title is required (minimum 4 characters)";
        break;
      case 2: {
        const catInput = form.querySelector(
          '[name*="[product_category_id]"]',
        ) as HTMLInputElement | null;
        if (!catInput?.value) error = "Please select a category";
        break;
      }
      case 6: {
        const uomSelect = form.querySelector(
          '[name*="[default_price_uom]"]',
        ) as HTMLSelectElement | null;
        if (!uomSelect?.value) error = "Please select a price unit of measure";
        break;
      }
    }

    if (error) {
      this.showStepError(error);
      return false;
    }

    this.clearStepError();
    return true;
  }

  private showStepError(message: string) {
    if (this.hasStepErrorTarget) {
      this.stepErrorTarget.textContent = message;
      this.stepErrorTarget.classList.remove("hidden");
    }
  }

  private loadUomData() {
    try {
      this.uomClassification = JSON.parse(
        this.uomDataTarget.textContent || "{}",
      );
    } catch {
      this.uomClassification = { measurable: [], countable: [] };
    }
  }

  get uomData(): UomClassification {
    return this.uomClassification || { measurable: [], countable: [] };
  }
}
